import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import { AdminOverview } from './AdminOverview';
import { GuestManagement } from './GuestManagement';
import { EventManagement } from './EventManagement';
import { RequestManagement } from './RequestManagement';
import { MediaManagement } from './MediaManagement';
import { AnalyticsDashboard } from './AnalyticsDashboard';
import { AdminPinManager } from './AdminPinManager';
import { PinAuditLogViewer } from './PinAuditLogViewer';
import { SiteCustomizationManager } from './SiteCustomizationManager';
import { ClothingInventoryManagement } from './ClothingInventoryManagement';
import { MessageBoard } from './MessageBoard';
import { PostDetail } from './PostDetail';
import { CreatePostForm, PostFormData } from './CreatePostForm';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `admin-tab-${index}`,
    'aria-controls': `admin-tabpanel-${index}`,
  };
}

export const AdminDashboard: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminUserId, setAdminUserId] = useState<string>('');
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [createPostDialogOpen, setCreatePostDialogOpen] = useState(false);
  const [messageBoardRefreshKey, setMessageBoardRefreshKey] = useState(0);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      // Check if user is authenticated via Cognito
      const token = localStorage.getItem('authToken');
      const user = localStorage.getItem('authUser');
      
      if (!token || !user) {
        setError('Authentication required');
        return;
      }

      const userData = JSON.parse(user);
      
      // Check if user has admin role or admin groups
      if (userData.role !== 'admin' && !userData.groups?.includes('admins')) {
        setError('Admin access required');
        return;
      }
      
      // Store admin user ID for message board
      setAdminUserId(userData.sub || userData.id || '');

      // Backend API is partially available, use hybrid mode
      console.log('Using hybrid mode - real API where available, demo data as fallback');
      setIsAdmin(true);
      
      // Set API base URL for components to use
      (window as any).API_BASE_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';
      
      // Enable hybrid mode (some real API, some demo)
      (window as any).DEMO_MODE = false;
      (window as any).HYBRID_MODE = true;
      
    } catch (err) {
      setError('Authentication error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleNavigateToTab = (tabIndex: number) => {
    setCurrentTab(tabIndex);
  };

  const handleViewPost = (postId: string) => {
    setSelectedPostId(postId);
  };

  const handleBackToMessages = () => {
    setSelectedPostId(null);
    // Refresh message board when returning from post detail
    setMessageBoardRefreshKey(prev => prev + 1);
  };

  const handleCreatePost = async (data: PostFormData) => {
    try {
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      const API_URL = process.env.NODE_ENV === 'production'
        ? 'https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod'
        : 'http://localhost:3001';

      const response = await fetch(`${API_URL}/api/message-board/posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error('Failed to create post');
      }

      setCreatePostDialogOpen(false);
      // Trigger MessageBoard refresh
      setMessageBoardRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('Error creating post:', err);
      throw err;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !isAdmin) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          {error || 'Access denied'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {(window as any).DEMO_MODE && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Demo Mode:</strong> You're viewing the admin dashboard with sample data. 
          The backend server is not running, so all data shown is for demonstration purposes only.
        </Alert>
      )}
      {!(window as any).DEMO_MODE && !(window as any).HYBRID_MODE && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <strong>Live Mode:</strong> Connected to backend API. All data is real and changes will be saved.
        </Alert>
      )}
      {(window as any).HYBRID_MODE && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Hybrid Mode:</strong> Using real backend where available, demo data as fallback. Some features may show sample data.
        </Alert>
      )}
      <Typography variant="h4" component="h1" gutterBottom>
        Admin Dashboard
      </Typography>
      
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange} 
            aria-label="admin dashboard tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Overview" {...a11yProps(0)} />
            <Tab label="Guest Management" {...a11yProps(1)} />
            <Tab label="PIN Management" {...a11yProps(2)} />
            <Tab label="PIN Audit Logs" {...a11yProps(3)} />
            <Tab label="Event Management" {...a11yProps(4)} />
            <Tab label="Requests" {...a11yProps(5)} />
            <Tab label="Media" {...a11yProps(6)} />
            <Tab label="Analytics" {...a11yProps(7)} />
            <Tab label="Clothing Inventory" {...a11yProps(8)} />
            <Tab label="Message Board" {...a11yProps(9)} />
            <Tab label="Site Customization" {...a11yProps(10)} />
          </Tabs>
        </Box>
        
        <TabPanel value={currentTab} index={0}>
          <AdminOverview onNavigateToTab={handleNavigateToTab} />
        </TabPanel>
        
        <TabPanel value={currentTab} index={1}>
          <GuestManagement />
        </TabPanel>
        
        <TabPanel value={currentTab} index={2}>
          <AdminPinManager />
        </TabPanel>
        
        <TabPanel value={currentTab} index={3}>
          <PinAuditLogViewer />
        </TabPanel>
        
        <TabPanel value={currentTab} index={4}>
          <EventManagement />
        </TabPanel>
        
        <TabPanel value={currentTab} index={5}>
          <RequestManagement />
        </TabPanel>
        
        <TabPanel value={currentTab} index={6}>
          <MediaManagement />
        </TabPanel>
        
        <TabPanel value={currentTab} index={7}>
          <AnalyticsDashboard />
        </TabPanel>
        
        <TabPanel value={currentTab} index={8}>
          <ClothingInventoryManagement />
        </TabPanel>
        
        <TabPanel value={currentTab} index={9}>
          {selectedPostId ? (
            <PostDetail
              postId={selectedPostId}
              currentUserId={adminUserId}
              isAdmin={true}
              onBack={handleBackToMessages}
            />
          ) : (
            <MessageBoard
              key={messageBoardRefreshKey}
              onCreatePost={() => setCreatePostDialogOpen(true)}
              onViewPost={handleViewPost}
            />
          )}
        </TabPanel>
        
        <TabPanel value={currentTab} index={10}>
          <SiteCustomizationManager />
        </TabPanel>
      </Paper>

      {/* Create Post Dialog */}
      <CreatePostForm
        open={createPostDialogOpen}
        onClose={() => setCreatePostDialogOpen(false)}
        onSubmit={handleCreatePost}
        isAdmin={true}
      />
    </Container>
  );
};